import { RoofData } from '../types';
import { makeRasterPanelKeyFromTopLeftCorner, numberInRange, suggestAllingmentBasedUponMaxPanelSizes, getPercentage } from '../../../../util';
import {
	ENERGY_THRESHOLD_THAT_A_PANEL_MUST_HAVE,
	FACET_TYPE,
	FLAT_ROOF_DEFAULT_PITCH,
	GROUND_MOUNT_DEFAULT_PITCH,
	IMAGERY_PROVIDER,
	IMAGERY_PROVIDER_KEY,
	MIN_PANELS_REQUIRED_FOR_DESIGN_TO_QUALIFY,
} from '../../../../constants';

function seLectPanelsFromWHoleRoofSegIfItsOnlyOnePanelWasSelected(
	allPanelsSelectedByBillSize: RasterSolarPanel[],
	rasterRoofSegs: { [key: string]: RasterRoofSegment }
) {
	const roofSegmentObjSuggestedPanels = new Set<string>();
	const keys: string[] = [];
	for (let i = 0; i < allPanelsSelectedByBillSize.length; i++) {
		const index = allPanelsSelectedByBillSize[i].segmentId;
		if (!roofSegmentObjSuggestedPanels.has(index)) {
			roofSegmentObjSuggestedPanels.add(index);
			keys.push(index);
		}
	}
	let reselectPanels: RasterSolarPanel[] = [];
	keys.forEach(key => {
		reselectPanels = [...reselectPanels, ...rasterRoofSegs[key].panels];
	});
	return reselectPanels;
}

// discard a facet if its panel count is below minPanelsRequiredForDesignToQualify (from the min-panel query param)

function getFilterFunction(toggleValue: boolean, minPanelsRequiredForDesignToQualify: number) {
	if (toggleValue) {
		return (roofSegs: RasterRoofSegment[]) => {
			return filterRasterSegmentsWithFlatRoof(roofSegs).filter(r => r.panelsCount >= minPanelsRequiredForDesignToQualify);
		};
	}
	return (roofSegs: RasterRoofSegment[]) => filterRasterSegmentsWithAllfilterConditions(roofSegs, minPanelsRequiredForDesignToQualify);
}

function filterRasterSegments(roofSegs: RasterRoofSegment[], minPanelsRequiredForDesignToQualify: number) {
	roofSegs.forEach(roofSeg => {
		if (roofSeg.panelsCount < minPanelsRequiredForDesignToQualify) return;
		let energyToSubtract = 0;
		let panelsHavingEnergyHigherThenThresholdVal = false;

		for (let i = 0; i < roofSeg.panels.length; i++) {
			let count = 0;
			if (roofSeg.panels[i].yearlyEnergyDcKwh >= ENERGY_THRESHOLD_THAT_A_PANEL_MUST_HAVE)
				count += 1;

			if (count >= minPanelsRequiredForDesignToQualify) {
				panelsHavingEnergyHigherThenThresholdVal = true;
				break;
			}

		}

		if (panelsHavingEnergyHigherThenThresholdVal) {
			roofSeg.panels = roofSeg.panels.filter(p => {
				if (p.yearlyEnergyDcKwh < ENERGY_THRESHOLD_THAT_A_PANEL_MUST_HAVE) {
					energyToSubtract += p.yearlyEnergyDcKwh;
					return false;
				}
				return true;
			});
			roofSeg.yearlyEnergyDcKwh -= energyToSubtract;
		}
	});
	return roofSegs.filter(roofSeg => roofSeg.panelsCount >= minPanelsRequiredForDesignToQualify).filter(r => !r?.softDeleted);
}

/**returns all user-mapped segments and all auto-generated segments with pitch>8, 
	if usermapped, dont filter out less than 8 pitch;
*/
function filterRasterSegmentsWithFlatRoof(roofSegs: RasterRoofSegment[]) {
	return roofSegs.filter(r => (r.pitchDegrees > 8 || r?.isGroundMount || !!r.userMapped));
}

function filterRasterSegmentsWithAllfilterConditions(roofSegs: RasterRoofSegment[], minPanelsRequiredForDesignToQualify: number) {
	const filteredRoofSegs = filterRasterSegments(roofSegs, minPanelsRequiredForDesignToQualify);
	return filteredRoofSegs.filter(roofSeg => {
		const validAzimuth = !(numberInRange(roofSeg.azimuthDegrees, 320, 360))
			&& !(numberInRange(roofSeg.azimuthDegrees, 0, 40));
		return validAzimuth && (roofSeg.pitchDegrees > 8 || roofSeg?.isGroundMount || roofSeg.userMapped);
	});
}
function sortRoofSegments(roofSegs: RasterRoofSegment[]) {
	// if panelCount > 6 sort acc to azim which is closet to 180
	// else sort acc to yearly enrgy of roofSegment
	// roofSegs.sort((a, b) => {
	// 	if (a.panelsCount < 6 || b.panelsCount < 6) {
	// 		return b.yearlyEnergyDcKwh - a.yearlyEnergyDcKwh;
	// 	}
	// 	const aAzimDiff = Math.abs(a.azimuthDegrees - 180);
	// 	const bAzimDiff = Math.abs(b.azimuthDegrees - 180);
	// 	return aAzimDiff - bAzimDiff;
	// });
	// most effiecent roofSeg shows up first;
	roofSegs.sort((r1, r2) => {
		return r2.yearlyEnergyDcKwh / r2.panelsCount - r1.yearlyEnergyDcKwh / r1.panelsCount;
	});

	roofSegs.forEach(a => {
		a.panels = sortSolarPanels(a.panels);
	});

}

function convertUnslectedPanelsToSelectedPanels(allPanels: RasterSolarPanel[], unselectedPanels: string[]) {

	let totalMatchingPanels = 0;
	const unselectedPanelsCoordinate = new Set(unselectedPanels);
	const selectedPanels = [];

	for (let i = 0; i < allPanels.length; i++) {
		const exteriorCoord = makeRasterPanelKeyFromTopLeftCorner(allPanels[i].exteriorCoords[0]);
		if (unselectedPanelsCoordinate.has(exteriorCoord)) totalMatchingPanels++;
		else selectedPanels.push(allPanels[i].id);
	}
	// if not one of them belongs here that means panel config has changed
	// and we want to set the selected panels that are set by recomendation algo
	// find what to do when both have zero panels in them
	if (!totalMatchingPanels && !unselectedPanelsCoordinate.size) return [];
	if (totalMatchingPanels !== unselectedPanelsCoordinate.size) return [];
	return selectedPanels;
}

function checkIfAllSelectedPanelsExist(allPanels: RasterSolarPanel[], selectedPanels: string[]) {

	let totalMatchingPanels = 0;
	const selectedPanelsCoordinate = new Set(selectedPanels);

	for (let i = 0; i < allPanels.length; i++) {
		if (selectedPanelsCoordinate.has(allPanels[i].id)) totalMatchingPanels++;
	}
	// if not one of them belongs here that means panel config has changed
	// and we want to set the selected panels that are set by recomendation algo
	console.log('totalMatchingPan', selectedPanelsCoordinate, totalMatchingPanels);
	if (totalMatchingPanels !== selectedPanelsCoordinate.size) return [];
	return selectedPanels;
}
function getCenterCoord(e: number[][]) {
	const x = e.slice(0, 4).map(a => a[0]).reduce((p: number, c: number) => p + c, 0) / 4;
	const y = e.slice(0, 4).map(a => a[1]).reduce((p: number, c: number) => p + c, 0) / 4;
	return { x, y };
}

// If the result is negative, a is sorted before b.
// If the result is positive, b is sorted before a.
// If the result is 0, no changes are done with the sort order of the two values.

// take distance into consideration too while sorting on the basis of eneergy;
function sortSolarPanels(panels: RasterSolarPanel[]) {
	if (panels.length <= 1) return panels;
	const highestEnergyPanel = panels.reduce((p, c) => p.yearlyEnergyDcKwh > c.yearlyEnergyDcKwh ? p : c, panels[0]);
	const highestEnergyCoord = getCenterCoord(highestEnergyPanel.exteriorCoords);
	const distanceThreshhold = 3;
	const secondDistanceThreshold = 5;
	panels.sort((a, b) => {
		const centerCoorA = getCenterCoord(a.exteriorCoords);
		const centerCoorB = getCenterCoord(b.exteriorCoords);
		const distanceA = Math.sqrt((centerCoorA.x - highestEnergyCoord.x) ** 2 + (centerCoorA.y - highestEnergyCoord.y) ** 2);
		const distanceB = Math.sqrt((centerCoorB.x - highestEnergyCoord.x) ** 2 + (centerCoorB.y - highestEnergyCoord.y) ** 2);
		const bothPanelsAreNear = distanceA < distanceThreshhold && distanceB < distanceThreshhold;
		const onlyPanelAisCloser = distanceA < distanceThreshhold && distanceB > distanceThreshhold;
		const onlyPanelBisCloser = distanceA > distanceThreshhold && distanceB < distanceThreshhold;
		const energyAIsGreater = a.yearlyEnergyDcKwh >= b.yearlyEnergyDcKwh;
		const energyBIsGreater = !energyAIsGreater;

		if (bothPanelsAreNear) {
			const val = energyAIsGreater ? -1 : 1;
			return val;
		}

		if (onlyPanelAisCloser) {
			if (energyAIsGreater) return -1;
			if (distanceB < secondDistanceThreshold) return 1;
			return -1;
		}

		if (onlyPanelBisCloser) {
			if (energyBIsGreater) return 1;
			if (distanceA < secondDistanceThreshold) return -1;
			return 1;
		}

		const val = distanceA < distanceB ? -1 : 1;
		return val;
	});
	return panels;
}

export function convertRawRoofDataToContextData(roofData: RawSolarQueryData, minPanelFromQueryParam?: number): RoofData{
	const minPanelsRequiredForDesignToQualify = minPanelFromQueryParam ?? MIN_PANELS_REQUIRED_FOR_DESIGN_TO_QUALIFY;
	console.log('[MinPanelsDebug] minPanelFromQueryParam (subhub/parent):', minPanelFromQueryParam, 'minPanelsRequiredForDesignToQualify (used):', minPanelsRequiredForDesignToQualify);
	const imageryInfo = roofData[IMAGERY_PROVIDER_KEY[roofData.source as ImagerySource] as keyof RawSolarQueryData] as SolarInfo;
	const allRoofSegs = structuredClone(roofData.designProcessedData.solarPanels.roofSegments);

	return {
		allPanels: [],
		jpgUrl: imageryInfo.imageUrl,
		dsmUrl: imageryInfo.dsmUrl,
		boundingBox: imageryInfo.metaData.boundingBox,
		latLng: imageryInfo.latLng,
		selectedPanels: [],
		uuid: roofData.designUUID,
		externalId: roofData.externalID,
		roofSegs: {},
		allRoofSegs,
		imgWidth: imageryInfo.metaData.imgWidth || 600,
		imgHeight: imageryInfo.metaData.imgHeight || 600,
		rasterSolarPanels: [],
		roofSegsFromWhichUserHasSelectedPanels: [],
		org: {
			setting: {
				showToggleForAllPanels: roofData.organization.settings?.showToggleForAllPanels || false,
				acceptsAnnualEnergy: roofData.organization.settings?.acceptsAnnualEnergy || false,
				enableOffsetWarning: roofData.organization.settings?.enableOffsetWarning || false,
				isB2C: roofData.organization.settings?.isB2C || false,
				canEdit: !!roofData.organization.settings?.canEdit,
				canToggle3d: roofData.organization.settings?.canToggle3d !== undefined
					? roofData.organization.settings.canToggle3d
					: true,
				dataForTool: roofData.organization.settings?.dataForTool,
				defaultFlatPitchDeg: roofData.organization.designSettings?.defaultFlatPitchDeg ?? GROUND_MOUNT_DEFAULT_PITCH,
				defaultDrawnFacetType: (roofData.organization.designSettings?.defaultDrawnFacetType as FACET_TYPE) ?? null,
				minPanelsRequiredForDesignToQualify,
			},
			panelKeys: roofData.organization?.panelKeys || [],
			configurations: roofData.organization.configurations,
		},
		panel: {
			wattage: roofData.designProcessedData.solarPanels.wattage,
			key: roofData.designProcessedData.solarPanels.panelTypeKey,
			height: roofData.designProcessedData.solarPanels.height,
			width: roofData.designProcessedData.solarPanels.width,
			panelName: roofData.designProcessedData.solarPanels.panelTypeTitle,
		},
		showAllPanels: false,
		selectedPanelKey: roofData.panelKey,
		imagerySource: roofData.source || IMAGERY_PROVIDER.SUNROOF,
		recommendedPanels: [],
		mode: roofData.mode,
		obstructions: roofData.designProcessedData.obstructions || [],
		isSunroofAvailable: roofData.isSunroofAvailable,
		missingRoofPolygon: !!roofData.designProcessedData.solarPanels.missingRoofPolygon,
		createdAt: roofData.createdAt,
		updatedAt: roofData.updatedAt,
		imageryCapturedDate: roofData.designProcessedData.imageryCapturedDate,
		solarDataCapturedDate: roofData.designProcessedData.solarDataCapturedDate,
		imageryQuality: roofData.solarInfo?.imageryQuality,
		autoSwitchStatus: roofData.autoSwitchStatus,
		billingLogs: roofData.billingLogs,
		modeAndItsCorrespondingImagery: roofData.modeAndItsCorrespondingImagery,
		designType: roofData.designType,
		lidarCapturedDate: roofData.designProcessedData.lidarCapturedDate,
		roofDesignMode: roofData.roofDesign?.mode,
		roofDesignSource: roofData.roofDesign?.source,
		roofDesignModeAndItsCorrespondingImagery: roofData.roofDesign?.modeAndItsCorresPondingImagery,
		groundMetersPerPixel: imageryInfo.metaData.groundMetersPerPixel,
		specialEdges: roofData.designProcessedData.specialEdges,
		houseBoundingBox: roofData.designProcessedData.houseBoundingBox,
		settings: roofData.settings,
		gutterSetback: roofData.gutterSetback,
		nonGutterSetback: roofData.nonGutterSetback,
		obstructionSetback: roofData.obstructionSetback,
		panelsKeysForWhichDesignIsAvailable: roofData.designProcessedData.panelsKeysForWhichDesignIsAvailable,
		customMaps: roofData.customMaps,
		showAutoDetectedEdges: roofData.designProcessedData.showAutoDetectedEdges,
		allGutterLines: roofData.designProcessedData.gutterLines ?? [],
		dsmEdits: roofData.designProcessedData.shadingAnalysis?.dsmEdits ?? [],
		shadingAnalysis: {
			...roofData.designProcessedData?.shadingAnalysis
		}
	};
}

export function convertDataToRoofContextData(
	roofData: RawSolarQueryData,
	annualEnergy: number,
	toggle: { orgToggle: boolean, designToggle: boolean },
	energyDerateRate: number,
	source: ImagerySource,
	minPanelFromQueryParam?: number):
	RoofData {
	const minPanelsRequiredForDesignToQualify = minPanelFromQueryParam ?? MIN_PANELS_REQUIRED_FOR_DESIGN_TO_QUALIFY;
	console.log('[MinPanelsDebug] minPanelFromQueryParam:', minPanelFromQueryParam);
	console.log('[MinPanelsDebug] minPanel value being used:', minPanelsRequiredForDesignToQualify);
	roofData.designProcessedData.solarPanels.roofSegments.map(eachRoofSeg => {
		const suggestedCount = eachRoofSeg.panels.reduce((acc:number, val:any) => acc += !val.userMapped ? 1 : 0,0);
		if(suggestedCount < minPanelsRequiredForDesignToQualify) {
			eachRoofSeg.panels = eachRoofSeg.panels.filter((eachPanel:any) => eachPanel.userMapped);
			eachRoofSeg.panelsCount = eachRoofSeg.panels.length;
		}
		return eachRoofSeg;
	});

	console.log('roofData:', roofData);
	// This function
	// 1. apply filters based on toggles and some validations(based on azimuth, panel counts, show all panels etc) 
	// 2. then it sorts the roof segs based on descending order of production/#panels
	// 3. then it gets all the panels filtered and sorted after the above steps.
	// NOTE: Keep in mind the panels coming from BE is sorted based on energy and how close the panels 
	// 			 are to each other 
	// 4. then it creates a map of segID and roofsegs for further processing
	// 5. then it gets the index of all selected and unselected panels from allPanels[], based on 
	// 		condn. that all panels sum till total derated energy will constitute selected panels and 
	// 		unselected will be those coming after that till +50% derated energy.
	// 6. Then check if we have selected panels if :
	// 		yes => then check all the selected panels exists
	//    no => then it tries to get selected panels from unselected panels by filtering unselected ones
	// 7. If we have still no selected panels then we will choose above selected panels from step 5.
	// 		and apply seLectPanelsFromWHoleRoofSegIfItsOnlyOnePanelWasSelected on it.
	console.log('convertDataToRoofContextData');
	const imageryInfo = roofData[IMAGERY_PROVIDER_KEY[source] as keyof RawSolarQueryData] as SolarInfo;
	const allRoofSegs = structuredClone(roofData.designProcessedData.solarPanels.roofSegments);
	roofData.designProcessedData.solarPanels.roofSegments =
		getFilterFunction(toggle.orgToggle, minPanelsRequiredForDesignToQualify)(roofData.designProcessedData.solarPanels.roofSegments);

	// sorting acc to azim
	sortRoofSegments(roofData.designProcessedData.solarPanels.roofSegments);

	let allPanels: RasterSolarPanel[] = [];
	roofData.designProcessedData.solarPanels.roofSegments.map(rs => {
		allPanels = allPanels.concat(rs.panels.flat());
	});

	const rasterRoofSegs: { [key: string]: RasterRoofSegment } = {};
	roofData.designProcessedData.solarPanels.roofSegments.map(rs => {
		if (rs.panels.length) rasterRoofSegs[rs.panels[0].segmentId] = rs;
	});

	const {
		selectedPanelsIndex,
		unselectedPanelsIndex
	} = suggestAllingmentBasedUponMaxPanelSizes(
		allPanels.map(p => Math.floor(p.yearlyEnergyDcKwh)),
		// bumping up by 67% to counter out derating
		annualEnergy + getPercentage(annualEnergy, energyDerateRate), annualEnergy + getPercentage(annualEnergy, 50 + energyDerateRate)
	);

	// collection of slected and unslected
	console.log('allPanels length', allPanels.length);
	const commonPanels: RasterSolarPanel[] = toggle.designToggle ?
		[...allPanels] : [...selectedPanelsIndex, ...unselectedPanelsIndex].map(i => allPanels[i]);
	const newSuggestedPanels = seLectPanelsFromWHoleRoofSegIfItsOnlyOnePanelWasSelected(
		commonPanels, rasterRoofSegs,
	);
	console.log(roofData);
	console.log(`${roofData.selectedPanels?.length > 0 ? 'checkIfAllSelectedPanelsExist':'convertUnslectedPanelsToSelectedPanels' }`);

	// choose between selected or unslec panles
	// if slected panelssetBillAmount are not null choose them else choose unslected panels
	let selectedPanels = (roofData.selectedPanels?.length > 0) ?
		checkIfAllSelectedPanelsExist(allPanels, roofData.selectedPanels) :
		convertUnslectedPanelsToSelectedPanels(allPanels, roofData.unselectedPanels);

	// getting unselected panels on the basis of wheter is it first time that
	// we are generating them or are we getting them from the db

	const userHasSavedData = Object.keys(roofData.productionData).length !== 0;
	if (!userHasSavedData || !selectedPanels.length) {
		// dont over push it if we reach here
		selectedPanels = [];
		for (let i = 0; i < newSuggestedPanels.length; i++) {
			if (selectedPanelsIndex.includes(i))
				selectedPanels.push(newSuggestedPanels[i].id);
		}
	}

	const segsFromWhichUserSelectedPanels = new Set<string>();
	const roofSegsFromWhichUserHasSelectedPanels = [];
	for (let i = 0; i < newSuggestedPanels.length; i++) {
		const roofId = newSuggestedPanels[i].segmentId;
		if (!segsFromWhichUserSelectedPanels.has(roofId)) {
			roofSegsFromWhichUserHasSelectedPanels.push(roofId);
			segsFromWhichUserSelectedPanels.add(roofId);
		}
	}
	const { selectedPanels: recommendedPanels } = reCalculateSelectedPanels(allPanels, rasterRoofSegs, annualEnergy, toggle, energyDerateRate);
	return {
		allPanels,
		jpgUrl: imageryInfo.imageUrl,
		dsmUrl: imageryInfo.dsmUrl,
		boundingBox: imageryInfo.metaData.boundingBox,
		latLng: imageryInfo.latLng,
		selectedPanels,
		uuid: roofData.designUUID,
		externalId: roofData.externalID,
		roofSegs: rasterRoofSegs,
		allRoofSegs,
		imgWidth: imageryInfo.metaData.imgWidth || 600,
		imgHeight: imageryInfo.metaData.imgHeight || 600,
		rasterSolarPanels: newSuggestedPanels,
		roofSegsFromWhichUserHasSelectedPanels,
		panel: {
			wattage: roofData.designProcessedData.solarPanels.wattage || 0.4,
			key: roofData.designProcessedData.solarPanels.panelTypeKey || '400W',
			height: roofData.designProcessedData.solarPanels.height,
			width: roofData.designProcessedData.solarPanels.width,
			panelName: roofData.designProcessedData.solarPanels.panelTypeTitle,
		},
		org: {
			setting: {
				showToggleForAllPanels: roofData.organization.settings?.showToggleForAllPanels || false,
				acceptsAnnualEnergy: roofData.organization.settings?.acceptsAnnualEnergy || false,
				enableOffsetWarning: roofData.organization.settings?.enableOffsetWarning || false,
				isB2C: roofData.organization.settings?.isB2C || false,
				canEdit: !!roofData.organization.settings?.canEdit,
				canToggle3d: roofData.organization.settings?.canToggle3d !== undefined
					? roofData.organization.settings.canToggle3d
					: true,
				dataForTool: roofData.organization.settings?.dataForTool,
				defaultFlatPitchDeg: roofData.organization.designSettings?.defaultFlatPitchDeg ?? GROUND_MOUNT_DEFAULT_PITCH,
				defaultDrawnFacetType: (roofData.organization.designSettings?.defaultDrawnFacetType as FACET_TYPE) ?? null,
				minPanelsRequiredForDesignToQualify,
			},
			panelKeys: roofData.organization?.panelKeys || [],
			configurations: roofData.organization.configurations,
		},
		showAllPanels: roofData.showToggleForAllPanels,
		selectedPanelKey: roofData.panelKey,
		imagerySource: roofData.source || IMAGERY_PROVIDER.SUNROOF,
		recommendedPanels: recommendedPanels,
		setbacks: roofData.setbacks,
		mode: roofData.mode,
		obstructions: roofData.designProcessedData.obstructions || [],
		isSunroofAvailable: roofData.isSunroofAvailable,
		missingRoofPolygon: !!roofData.designProcessedData.solarPanels.missingRoofPolygon,
		createdAt: roofData.createdAt,
		updatedAt: roofData.updatedAt,
		imageryCapturedDate: roofData.designProcessedData.imageryCapturedDate,
		solarDataCapturedDate: roofData.designProcessedData.solarDataCapturedDate,
		imageryQuality: roofData.solarInfo?.imageryQuality,
		autoSwitchStatus: roofData.autoSwitchStatus,
		billingLogs: roofData.billingLogs,
		modeAndItsCorrespondingImagery: roofData.modeAndItsCorrespondingImagery,
		designType: roofData.designType,
		roofDesignMode: roofData.roofDesign?.mode,
		roofDesignSource: roofData.roofDesign?.source,
		roofDesignModeAndItsCorrespondingImagery: roofData.roofDesign?.modeAndItsCorresPondingImagery,
		groundMetersPerPixel: imageryInfo.metaData.groundMetersPerPixel,
		specialEdges: roofData.designProcessedData.specialEdges,
		houseBoundingBox: roofData.designProcessedData?.houseBoundingBox,
		settings: roofData.settings,
		gutterSetback: roofData.gutterSetback,
		nonGutterSetback: roofData.nonGutterSetback,
		obstructionSetback: roofData.obstructionSetback,
		panelsKeysForWhichDesignIsAvailable: roofData.designProcessedData.panelsKeysForWhichDesignIsAvailable,
		customMaps: roofData.customMaps,
		showAutoDetectedEdges: roofData.designProcessedData.showAutoDetectedEdges,
		allGutterLines: roofData.designProcessedData.gutterLines ?? [],
		dsmEdits: roofData.designProcessedData.shadingAnalysis?.dsmEdits ?? [],
		shadingAnalysis: {
			...roofData.designProcessedData?.shadingAnalysis
		}
	};
}

export function getPreviousPanelAllignment(
	roofData: RawSolarQueryData,
	annualEnergy: number,
	toggle: { orgToggle: boolean, designToggle: boolean },
	energyDerateRate: number,
	source: ImagerySource,
	minPanelFromQueryParam?: number,
	selectedPanelsOnUi?: string[],
	selectedRoofIndex?: string,
	doesUserPerformedFacetDraw?: boolean,
	editShadingPerformed?: boolean,
	retainPanelSelectionOnUi?: boolean,
):
	RoofData {
	const minPanelsRequiredForDesignToQualify = minPanelFromQueryParam ?? MIN_PANELS_REQUIRED_FOR_DESIGN_TO_QUALIFY;
	console.log('[MinPanelsDebug] minPanelFromQueryParam:', minPanelFromQueryParam);
	console.log('[MinPanelsDebug] minPanel value being used:', minPanelsRequiredForDesignToQualify);
	roofData.designProcessedData.solarPanels.roofSegments.map(eachRoofSeg => {
		const suggestedCount = eachRoofSeg.panels.reduce((acc:number, val:any) => acc += !val.userMapped ? 1 : 0,0);
		if(suggestedCount < minPanelsRequiredForDesignToQualify) {
			eachRoofSeg.panels = eachRoofSeg.panels.filter((eachPanel:any) => eachPanel.userMapped);
			eachRoofSeg.panelsCount = eachRoofSeg.panels.length;
		}
		return eachRoofSeg;
	});
	// This function takes the roofData coming from BE and try to get the previous saved alignment 
	// which user has saved, it follows these steps:
	// 1. apply filters based on toggles and some validations(based on azimuth, panel counts, show all panels etc) 
	// 2. then it sorts the roof segs based on descending order of production/#panels
	// 3. then it gets all the panels filtered and sorted after the above steps.
	// NOTE: Keep in mind the panels coming from BE is sorted based on energy and how close the panels 
	// are to each other 
	// 4. then it creates a map of segID and roofsegs for further processing
	// 5. then it gets a list of suggested panels in the  in a manner that if we have to select only
	//    one panels from roofsegs we will choose that and then we choose rest of the panels, then 
	//    we will choose another roofsegs first panel and then rest of its panels and so on...
	// 6. Then check if we have selected panels if :
	// 		yes => then check all the selected panels exists
	//    no => then it tries to get selected panels from unselected panels by filtering unselected ones
	// 7. If we till here dont have any selected panels we will try to get it by convertDataToRoofContextData.
	console.log('getPreviousPanelAllignment', 'editShadingPerformed:', editShadingPerformed);
	const imageryInfo = roofData[IMAGERY_PROVIDER_KEY[source] as keyof RawSolarQueryData] as SolarInfo;
	const allRoofSegs = structuredClone(roofData.designProcessedData.solarPanels.roofSegments);
	
	roofData.designProcessedData.solarPanels.roofSegments =
		getFilterFunction(toggle.orgToggle, minPanelsRequiredForDesignToQualify)(roofData.designProcessedData.solarPanels.roofSegments);

	// sorting acc to azim
	sortRoofSegments(roofData.designProcessedData.solarPanels.roofSegments);

	let allPanels: RasterSolarPanel[] = [];
	roofData.designProcessedData.solarPanels.roofSegments.map(rs => {
		allPanels = allPanels.concat(rs.panels.flat());
	});

	const rasterRoofSegs: { [key: string]: RasterRoofSegment } = {};
	roofData.designProcessedData.solarPanels.roofSegments.map(rs => {
		if (rs.panels.length) {
			rasterRoofSegs[rs.panels[0].segmentId] = rs;
		}
	});

	const newSuggestedPanels = seLectPanelsFromWHoleRoofSegIfItsOnlyOnePanelWasSelected(
		allPanels, rasterRoofSegs,
	);

	// console.log(`${roofData.selectedPanels?.length > 0 ? 'checkIfAllSelectedPanelsExist':'convertUnslectedPanelsToSelectedPanels' }`);
	let selectedPanels: string[] = [];
	selectedPanels = retainThePanelSelection(
		selectedPanelsOnUi, selectedRoofIndex, allPanels, doesUserPerformedFacetDraw, !!editShadingPerformed, !!retainPanelSelectionOnUi
	);
	const { selectedPanels: recommendedPanels } = reCalculateSelectedPanels(allPanels, rasterRoofSegs, annualEnergy, toggle, energyDerateRate);
	// selectedPanels = (roofData.selectedPanels && roofData.selectedPanels.length > 0) ?
	// 	checkIfAllSelectedPanelsExist(allPanels, roofData.selectedPanels) :
	// 	convertUnslectedPanelsToSelectedPanels(allPanels, roofData.unselectedPanels);
	
	// when wont we get selected panels if its a new design
	if (!selectedPanels.length) {
		// doing this beacuase roofData.solarInfo.processedData.solarPanels.roofSegments is geting updated in this func
		roofData.designProcessedData.solarPanels.roofSegments = allRoofSegs;
		// Pass the RAW query-param value through, not the already-resolved
		// minPanelsRequiredForDesignToQualify above — that local has already
		// fallen back to the static default once, and forwarding it here
		// would make convertDataToRoofContextData think the default value
		// came from the query param (same effective number either way,
		// since it re-resolves via the same fallback, but this keeps "did
		// the user actually pass a query param" accurate for logging).
		return convertDataToRoofContextData(roofData, annualEnergy, toggle, energyDerateRate, source, minPanelFromQueryParam);
	}
	const segsFromWhichUserSelectedPanels = new Set<string>();
	const roofSegsFromWhichUserHasSelectedPanels = [];
	for (let i = 0; i < newSuggestedPanels.length; i++) {
		const roofId = newSuggestedPanels[i].segmentId;
		if (!segsFromWhichUserSelectedPanels.has(roofId)) {
			roofSegsFromWhichUserHasSelectedPanels.push(roofId);
			segsFromWhichUserSelectedPanels.add(roofId);
		}
	}

	return {
		allPanels,
		jpgUrl: imageryInfo.imageUrl,
		dsmUrl: imageryInfo.dsmUrl,
		boundingBox: imageryInfo.metaData.boundingBox,
		latLng: imageryInfo.latLng,
		selectedPanels,
		uuid: roofData.designUUID,
		externalId: roofData.externalID,
		roofSegs: rasterRoofSegs,
		imgWidth: imageryInfo.metaData.imgWidth,
		imgHeight: imageryInfo.metaData.imgHeight,
		rasterSolarPanels: newSuggestedPanels,
		roofSegsFromWhichUserHasSelectedPanels,
		allRoofSegs,
		panel: {
			wattage: roofData.designProcessedData.solarPanels.wattage || 0.4,
			key: roofData.designProcessedData.solarPanels.panelTypeKey || '400W',
			height: roofData.designProcessedData.solarPanels.height,
			width: roofData.designProcessedData.solarPanels.width,
			panelName: roofData.designProcessedData.solarPanels.panelTypeTitle,
		},
		org: {
			setting: {
				showToggleForAllPanels: roofData.organization.settings?.showToggleForAllPanels || false,
				acceptsAnnualEnergy: roofData.organization.settings?.acceptsAnnualEnergy || false,
				enableOffsetWarning: roofData.organization.settings?.enableOffsetWarning || false,
				isB2C: roofData.organization.settings?.isB2C || false,
				canEdit: !!roofData.organization.settings?.canEdit,
				canToggle3d: roofData.organization.settings?.canToggle3d !== undefined
					? roofData.organization.settings.canToggle3d
					: true,
				dataForTool: roofData.organization.settings?.dataForTool,
				defaultFlatPitchDeg: roofData.organization.designSettings?.defaultFlatPitchDeg ?? GROUND_MOUNT_DEFAULT_PITCH,
				defaultDrawnFacetType: (roofData.organization.designSettings?.defaultDrawnFacetType as FACET_TYPE) ?? null,
				minPanelsRequiredForDesignToQualify,
			},
			panelKeys: roofData.organization?.panelKeys || [],
			configurations: roofData.organization.configurations,
		},
		showAllPanels: roofData.showToggleForAllPanels,
		selectedPanelKey: roofData.panelKey,
		imagerySource: roofData.source || IMAGERY_PROVIDER.SUNROOF,
		// isSyncedWithParent: roofData.isSyncedWithParent,
		mode: roofData.mode,
		recommendedPanels,
		setbacks: roofData.setbacks,
		obstructions: roofData.designProcessedData.obstructions || [],
		isSunroofAvailable: roofData.isSunroofAvailable,
		missingRoofPolygon: !!roofData.designProcessedData.solarPanels.missingRoofPolygon,
		createdAt: roofData.createdAt,
		updatedAt: roofData.updatedAt,
		imageryCapturedDate: roofData.designProcessedData.imageryCapturedDate,
		solarDataCapturedDate: roofData.designProcessedData.solarDataCapturedDate,
		imageryQuality: roofData.solarInfo?.imageryQuality,
		autoSwitchStatus: roofData.autoSwitchStatus,
		billingLogs: roofData.billingLogs,
		modeAndItsCorrespondingImagery: roofData.modeAndItsCorrespondingImagery,
		designType: roofData.designType,
		roofDesignMode: roofData.roofDesign?.mode,
		roofDesignSource: roofData.roofDesign?.source,
		roofDesignModeAndItsCorrespondingImagery: roofData.roofDesign?.modeAndItsCorresPondingImagery,
		groundMetersPerPixel: imageryInfo.metaData.groundMetersPerPixel,
		specialEdges: roofData.designProcessedData.specialEdges,
		houseBoundingBox: roofData.designProcessedData?.houseBoundingBox,
		settings: roofData.settings,
		gutterSetback: roofData.gutterSetback,
		nonGutterSetback: roofData.nonGutterSetback,
		obstructionSetback: roofData.obstructionSetback,
		panelsKeysForWhichDesignIsAvailable: roofData.designProcessedData.panelsKeysForWhichDesignIsAvailable,
		customMaps: roofData.customMaps,
		showAutoDetectedEdges: roofData.designProcessedData.showAutoDetectedEdges,
		allGutterLines: roofData.designProcessedData.gutterLines ?? [],
		dsmEdits: roofData.designProcessedData.shadingAnalysis?.dsmEdits ?? [],
		shadingAnalysis: {
			...roofData.designProcessedData?.shadingAnalysis
		}
	};
}
function retainThePanelSelection(selectedPanelsOnUi: string[] | undefined, selectedRoofIndex: string | undefined, allPanels: RasterSolarPanel[],
	doesUserPerformedFacetDraw: boolean | undefined, editShadingPerformed: boolean, retainPanelSelectionOnUi: boolean){
	// If no panels are selected in DB or panels are saved in DB and we have performed any edit
	// ops(rotate, delete, remove shading) on the facet assign the UI-selected panels after
	// removing panels which have been edited
	let selectedPanels: string[] = [];
	if (selectedPanelsOnUi?.length && selectedRoofIndex && !retainPanelSelectionOnUi) {
		console.log('from ui');
		allPanels.forEach((panel)=>{
			if((editShadingPerformed || panel.segmentId != selectedRoofIndex) && selectedPanelsOnUi.includes(panel.id)){
				selectedPanels.push(panel.id);
			}
		});
	}
	// If we have drawn a new facet, or the caller asked to retain the UI selection as-is
	// (e.g. edit tree → panel recommendation stays off), simply assign selectedPanelsOnUi
	else 	if((doesUserPerformedFacetDraw || retainPanelSelectionOnUi) && selectedPanelsOnUi){
		console.log('new facet');	
		selectedPanels = selectedPanelsOnUi;
	}
	return selectedPanels;
}

export function reCalculateSelectedPanels(
	allPanels: RasterSolarPanel[], rasterRoofSegs: { [key: number]: RasterRoofSegment }, annualEnergy: number,
	toggle: { orgToggle: boolean, designToggle: boolean }, energyDerateRate: number) {
	console.log('reCalculateSelectedPanels');
	const {
		selectedPanelsIndex,
		unselectedPanelsIndex
	} = suggestAllingmentBasedUponMaxPanelSizes(
		allPanels.map(p => Math.floor(p.yearlyEnergyDcKwh)),
		annualEnergy + getPercentage(annualEnergy, energyDerateRate), annualEnergy + getPercentage(annualEnergy, 50 + energyDerateRate)
	);

	// collection of slected and unslected
	console.log('allPanels length', allPanels.length);
	const commonPanels = toggle.designToggle ? [...allPanels] : [...selectedPanelsIndex, ...unselectedPanelsIndex].map(i => allPanels[i]);
	const newSuggestedPanels = seLectPanelsFromWHoleRoofSegIfItsOnlyOnePanelWasSelected(
		commonPanels, rasterRoofSegs,
	);

	const selectedPanels = [];
	for (let i = 0; i < newSuggestedPanels.length; i++) {
		if (selectedPanelsIndex.includes(i))
			selectedPanels.push(newSuggestedPanels[i].id);
	}

	const segsFromWhichUserSelectedPanels = new Set<string>();
	const roofSegsFromWhichUserHasSelectedPanels = [];
	for (let i = 0; i < newSuggestedPanels.length; i++) {
		const roofId = newSuggestedPanels[i].segmentId;
		if (!segsFromWhichUserSelectedPanels.has(roofId)) {
			roofSegsFromWhichUserHasSelectedPanels.push(roofId);
			segsFromWhichUserSelectedPanels.add(roofId);
		}
	}

	return {
		selectedPanels,
		rasterSolarPanels: newSuggestedPanels,
		roofSegsFromWhichUserHasSelectedPanels,
		recommendedPanels: selectedPanels,
	};
}

